import { useMemo } from 'react';

import {
  EXTERNAL_PLATFORM_ALLOWED_TEMPLATE_TYPES,
  useIsInExternalPlatform,
} from '@/shared/externalPlatform';
import { type UserInfo } from '@/types';
import { type MapTemplate, type MapTemplateType } from '@/types/MapTemplate';

import { type SectionKey } from '../constants';

/**
 * 맵 템플릿을 섹션별로 그룹화
 * @returns { NEW: MapTemplate[], FREE: MapTemplate[], BASIC: MapTemplate[], PRO: MapTemplate[] }
 * - NEW: hasNewTag 또는 isRecommended가 true인 맵들
 * - FREE/BASIC/PRO: templatePlanType에 따라 분류된 맵들
 * - 하나의 맵이 NEW와 플랜 섹션 모두에 포함될 수 있음
 */
const groupMapsBySection = (
  maps: MapTemplate[],
): Record<SectionKey, MapTemplate[]> => {
  const groupedMaps: Record<SectionKey, MapTemplate[]> = {
    NEW: [],
    FREE: [],
    BASIC: [],
    PRO: [],
  };
  for (const map of maps) {
    if (map.hasNewTag || map.isRecommended) {
      groupedMaps.NEW.push(map);
    }
    groupedMaps[map.templatePlanType].push(map);
  }
  return groupedMaps;
};

/**
 * New+Recommended > New > Recommended 순서 정렬
 * 동일 우선순위일 경우 priority 값 오름차순으로 정렬
 */
const mapSortFn = (a: MapTemplate, b: MapTemplate): number => {
  const getMapTagPriority = (map: MapTemplate): number => {
    if (map.hasNewTag && map.isRecommended) return 3;
    if (map.hasNewTag) return 2;
    if (map.isRecommended) return 1;
    return 0;
  };
  const priorityDiff = getMapTagPriority(b) - getMapTagPriority(a);
  return priorityDiff || a.priority - b.priority;
};

interface Props {
  maps: MapTemplate[];
  tab: MapTemplateType;
  user: UserInfo | null;
}

export const useOrganizeMaps = ({ maps, tab, user }: Props) => {
  const isInExternalPlatform = useIsInExternalPlatform();

  const filteredMaps = useMemo(
    () =>
      isInExternalPlatform
        ? maps.filter(map =>
            EXTERNAL_PLATFORM_ALLOWED_TEMPLATE_TYPES.includes(map.type),
          )
        : maps,
    [maps, isInExternalPlatform],
  );

  const mapsInTab = useMemo(
    () =>
      tab === 'total'
        ? filteredMaps
        : filteredMaps.filter(map => map.type === tab),
    [filteredMaps, tab],
  );

  const sortedGroupedMapsInTab = useMemo(() => {
    const groupedMapsInTab = groupMapsBySection(mapsInTab);
    for (const sectionKey in groupedMapsInTab) {
      groupedMapsInTab[sectionKey as SectionKey].sort(mapSortFn);
    }
    return groupedMapsInTab;
  }, [mapsInTab]);

  // 사용자 플랜 및 현재 탭에 따른 섹션 키
  const sortedSectionKeys = useMemo(() => {
    // Guest users default to FREE plan
    const planType = user?.planType ?? 'FREE';

    let sectionKeys: SectionKey[];
    if (planType === 'FREE') {
      sectionKeys = ['FREE', 'BASIC', 'PRO'];
    } else if (planType === 'BASIC') {
      sectionKeys = ['BASIC', 'FREE', 'PRO'];
    } else {
      sectionKeys = ['PRO', 'BASIC', 'FREE'];
    }
    if (tab === 'total') {
      sectionKeys = ['NEW', ...sectionKeys];
    }
    return sectionKeys.filter(
      sectionKey => 0 < sortedGroupedMapsInTab[sectionKey].length,
    );
  }, [user?.planType, tab, sortedGroupedMapsInTab]);

  return {
    mapsInTab,
    sectionKeys: sortedSectionKeys,
    groupedMapsInTab: sortedGroupedMapsInTab,
  };
};
